import { useMemo } from "react";

import { omit } from "lodash";

import { gql } from "@apollo/client";
import {
  type GetSalesReportFast,
  type GetSalesReportFastVariables,
} from "@north-beam/nb-client-side";
import { type IGranularity } from "@north-beam/nb-common";

import { type SalesReportProps, useSalesReport } from "./use-sales-report";

function transform(data: GetSalesReportFast | undefined) {
  const response = data?.me?.salesReportTable;
  if (!response) {
    return undefined;
  }
  const metricsKeys = response.rows.metricsKeys;

  return {
    ...response,

    rows: response.rows.rvs.map((row) => {
      const rolledUpCurrentMetrics: any = {};
      const rolledUpComparisonMetrics: any = row.metrics.comparison ? {} : null;

      metricsKeys.forEach((metricKey, indx) => {
        rolledUpCurrentMetrics[metricKey] = row.metrics.current[indx];
      });
      row.metrics.comparison &&
        metricsKeys.forEach((metricKey, indx) => {
          rolledUpComparisonMetrics[metricKey] = row.metrics.comparison[indx];
        });

      return {
        ...omit(row, "metrics"),
        metrics: {
          adKey: row.metrics.adKey,
          adKeyHash: row.metrics.adKeyHash,
          current: rolledUpCurrentMetrics,
          comparison: rolledUpComparisonMetrics,
        },
      };
    }),
  };
}

export const useSalesTableReport = (
  granularity: IGranularity,
  variables: SalesReportProps,
) => {
  const { data, ...rest } = useSalesReport<
    GetSalesReportFast,
    GetSalesReportFastVariables
  >(granularity, variables, GET_SALES_REPORT);

  const transformedData = useMemo(() => {
    return transform(data);
  }, [data]);
  return {
    ...rest,
    data: transformedData,
  };
};

const GET_SALES_REPORT = gql`
  query GetSalesReportFast(
    $dateRange: JSONObject!
    $attributionMethod: String!
    $attributionWindow: String!
    $compareDateRange: JSONObject
    $granularity: String
    $nbAccountingMode: String
    $timeGranularity: String
    $attributionWindows: [String!]
    $fakeCall: Boolean
  ) {
    me {
      salesReportTable(
        dateRange: $dateRange
        attributionMethod: $attributionMethod
        attributionWindow: $attributionWindow
        compareDateRange: $compareDateRange
        granularity: $granularity
        nbAccountingMode: $nbAccountingMode
        timeGranularity: $timeGranularity
        attributionWindows: $attributionWindows
        fakeCall: $fakeCall
      ) {
        rows {
          metricsKeys
          rvs {
            objectId
            nbtPlatformID
            link
            name
            dimensions
            extraDetails
            searchableStrings
            cellAnnotations
            metrics
            forecastV1
          }
        }
        startDate
        endDate
        hasV1Forecasts
        customGoals {
          displayName
          id
        }
      }
    }
  }
`;
